import type { IntercomUserType } from './IntercomUserType';
import type { OrgType } from './advisor-questionnaire/types';
import {
  EDWARD_JONES_EMAIL_DOMAIN,
  EDWARD_JONES_ORGANIZATION_DISPLAY_NAME,
  EDWARD_JONES_ORGANIZATION_NAME,
  GENWORTH_ORGANIZATION_DISPLAY_NAME,
  GENWORTH_ORGANIZATION_NAME,
  NEW_YORK_LIFE_EMAIL_DOMAIN,
  NEW_YORK_LIFE_ORGANIZATION_DISPLAY_NAME,
  NEW_YORK_LIFE_ORGANIZATION_NAME,
} from './constants';
import {
  NYL_COMMUNICATION_POLICY,
  type PilotCommunicationKey,
  type PilotCommunicationPolicy,
} from './pilotCommunications';

/**
 * A capability a partner program can withhold. Each value names one surface a
 * pilot advisor may be kept out of, and is passed to `<PilotRestricted>` at the
 * point the surface is rendered.
 */
export type PilotCapability =
  | 'add-partner'
  | 'anonymous-pui'
  | 'automatic-client-results'
  | 'carrier-bga'
  | 'client-reminder'
  | 'client-sharing'
  | 'crm-integration'
  | 'distribution-partners'
  | 'email-connection'
  | 'engagement-campaigns'
  | 'explore-quotes'
  | 'intake-form'
  | 'intake-form-data'
  | 'quick-edit-intake'
  | 'invite-client'
  | 'logo-branding'
  | 'org-type'
  | 'policies-comparison'
  | 'quick-start'
  | 'quote-apply'
  | 'seat-invitations'
  | 'sponsorship'
  | 'team-dashboard';

export type AdvisorBrandingMode = 'organization' | 'waterlily';

/** Copy for a capability that takes over a whole page rather than one control. */
export interface PilotBlockedPageCopy {
  heading: string;
  body: string;
}

/**
 * Replaces the generic "Fill Intake Form" surface with a self-plan form that records a
 * compliance acknowledgement. An optional `flagKey` gates the form separately from the
 * program flag; without one it is on for every advisor in the program.
 */
export interface PilotSelfPlanIntakeForm {
  flagKey?: FeatureFlagKeys;
  acknowledgementText: string;
  /** Identifies the acknowledgement in the ConsentRecords audit trail. */
  consentId: string;
  /**
   * Re-opened for the advisor's own self-plan clients only (`Client.isAdvisorSelfPlan`). Every
   * other client keeps the program's restriction.
   */
  reopensCapabilities?: readonly PilotCapability[];
}

/**
 * Requires a customer's own permission before an agent may enter that customer's information.
 *
 * Holds the partner-approved statements and the consent ids that pin them to the audit trail.
 * Deliberately has NO `flagKey`, unlike `PilotSelfPlanIntakeForm`: the feature is on exactly when
 * the advisor carries the program flag, and turning that off is the whole rollout control.
 */
export interface PilotAgentEnteredIntake {
  permissionText: string;
  acknowledgementText: string;
  /** Identifies the permission statement in the ConsentRecords audit trail. */
  permissionConsentId: string;
  /** Pins the catalogue revision independently of later edits to that consent row. */
  permissionConsentVersion: string;
  /** Identifies the acknowledgement statement in the ConsentRecords audit trail. */
  acknowledgementConsentId: string;
  /** Pins the catalogue revision independently of later edits to that consent row. */
  acknowledgementConsentVersion: string;
  /** Re-opened behind the consent gate. The raw surface stays withheld. */
  reopensCapabilities?: readonly PilotCapability[];
}

/**
 * An agreement the program's advisors must accept before they reach any part of the platform,
 * onboarding included. Each acceptance is an `AdvisorConsents` row keyed by
 * `consentVersionDescription`, so changing that value asks every advisor to accept again.
 *
 * No program carries one today. NYL's was taken off its program to switch the gate off entirely;
 * giving the program a `participationAgreement` again turns the gate, the accept endpoint and the
 * NYL metrics "Agreement Accepted" column back on.
 */
export interface PilotParticipationAgreement {
  title: string;
  text: string;
  checkboxLabel: string;
  consentVersionDescription: string;
  /** AppConfig switch that turns the gate on. Off holds no one; AppConfig unreadable holds everyone. */
  appConfigFlagKey: AppConfigFeatureFlagKey;
  /** Shown above the title. A path under the client's `public/` folder. */
  logo?: { src: string; alt: string };
}

export type PilotSignupIneligibleReason =
  | 'edj_advisor_invite_required'
  | 'nyl_advisor_invite_required';

/**
 * Makes new advisor accounts on the program's corporate email domain invitation-only. Client
 * sign-ups on the domain are never gated, so the partner's people can still plan for themselves.
 * Enforced by the server's `checkPilotAdvisorSignupEligibility`; an existing account always passes.
 */
export interface PilotAdvisorSignupGate {
  emailDomain: string;
  ineligibleReason: PilotSignupIneligibleReason;
}

export type SignupGatedPilotProgram = PilotProgram & { advisorSignupGate: PilotAdvisorSignupGate };

/** Where `computeAdvisorForceRedirect` sends an advisor who has not accepted, relative to /advisors/:advisorId. */
export const PARTICIPATION_AGREEMENT_REDIRECT_PATH = '/participation-agreement';

/**
 * Key in a seat's `metadata` naming the team invitation it was sent with. Only the pilot inviter's
 * combined invitation sets it (see the server's `pilotTeamSeatInvitation.ts`).
 */
export const SEAT_TEAM_INVITATION_METADATA_KEY = 'hierarchyInvitationId';

export interface PilotProgram {
  flagKey: FeatureFlagKeys;
  organizationDisplayName: string;
  organizationName: string;
  /** Fills the "… is preset for {label}" tooltip on locked organization fields. */
  lockedOrgLabel: string;
  /**
   * Preset organization type for the program's advisors, filled in during onboarding
   * instead of being asked. Whether they may then change it is a separate question,
   * answered by `org-type` appearing in `restrictedCapabilities`.
   */
  lockedOrgType?: OrgType;
  intercomAdvisorType?: IntercomUserType;
  intercomClientType?: IntercomUserType;
  /** Controls presentation branding without changing the stored organization identity. */
  advisorBrandingMode: AdvisorBrandingMode;
  /** Withheld capabilities. Empty means the program pins identity only. */
  restrictedCapabilities: readonly PilotCapability[];
  /** Withheld capabilities whose controls must be removed instead of rendered disabled. */
  hiddenCapabilities?: readonly PilotCapability[];
  restrictedTooltip: string;
  blockedPages?: Partial<Record<PilotCapability, PilotBlockedPageCopy>>;
  /** Advisor onboarding steps the program's advisors never see. */
  excludedOnboardingSteps?: readonly AdvisorOnboardingUIStep[];
  /**
   * Onboarding requirements the program excuses, for steps its advisors cannot satisfy.
   *
   * Read by the server when computing completion AND by the onboarding wizard when deciding
   * whether the step can be skipped, so the two cannot disagree about whether an advisor is
   * allowed to finish.
   */
  waivedOnboardingSteps?: readonly AdvisorOnboardingTrackingStep[];
  selfPlanIntakeForm?: PilotSelfPlanIntakeForm;
  agentEnteredIntake?: PilotAgentEnteredIntake;
  participationAgreement?: PilotParticipationAgreement;
  advisorSignupGate?: PilotAdvisorSignupGate;
  /**
   * Refuse withheld capabilities at the API as well as hiding them in the UI.
   *
   * Opt-in per program rather than on for all of them. Hiding a control and refusing
   * the request behind it are different guarantees, and only a program that committed
   * to the stronger one should pay for it: turning this on for a program that has been
   * running on UI gating alone would start rejecting calls its advisors can make today.
   */
  enforceRestrictionsAtApi?: boolean;
  /**
   * The partner's decision on each email it reviewed (see `pilotCommunications.ts`). Without one,
   * the program's advisors and their clients receive every email as before.
   */
  communications?: PilotCommunicationPolicy;
  /**
   * Marks an advisor as still in the program's advisor-only period, before client use is permitted.
   * Emails the partner placed in the client phase are held while it is set.
   */
  advisorOnlyPeriodFlagKey?: FeatureFlagKeys;
  /**
   * Keep the program's CLIENTS out of Intercom chat: no launcher and no in-app "Support" entry
   * point. Its advisors keep chat. Read through `isClientSupportChatWithheld`.
   */
  withholdsClientSupportChat?: boolean;
  /**
   * The program's CLIENTS may view the results and plan their agent shared, but not change them:
   * care assumptions, partners, care costs, family caregiving and funding are all withheld. Picking
   * a care setting stays open. Read through `isClientPlanReadOnly`.
   */
  clientPlanReadOnly?: boolean;
}

export const NYL_DEFAULT_HEALTHCARE_INFLATION_PERCENT = 3;
export const NYL_DEFAULT_HEALTHCARE_INFLATION_SOURCE_TEXT = 'Bureau of Labor Statistics Data';
export const NYL_DEFAULT_HEALTHCARE_INFLATION_SOURCE_URL =
  'https://data.bls.gov/timeseries/CUUR0000SA0L1E?output_view=pct_12mths';
export const NYL_DEFAULT_CLIENT_INTAKE_GUIDE: ClientIntakeGuideType = 'none';
export const NYL_PILOT_ADVISOR_FEATURE_FLAG = 'nylPilotAdvisor' satisfies FeatureFlagKeys;
export const PILOT_PROGRAM_SIGNUP_QUERY_PARAM = 'pilot_program';

export const NYL_AGENT_ENTERED_PERMISSION_TEXT =
  'NYL Agent authorization: I authorize my New York Life agent to enter information into Waterlily on my behalf. I will have the opportunity to review it and am responsible for its accuracy.';
export const NYL_AGENT_ENTERED_DATA_USE_TEXT =
  'Data use and sharing: I consent to the collection, use, and sharing of my data as described in Waterlily’s Privacy Policy and Terms of Service. This includes sharing my data with New York Life.';

const QUOTE_APPLY_BLOCKED_BODY =
  'To evaluate insurance options during the pilot, follow your standard processes outside of Waterlily.';

const TEAM_DASHBOARD_BLOCKED_BODY =
  'Team management is handled by your pilot coordinator during the pilot. Contact them to add or remove team members.';

const SPONSORSHIP_BLOCKED_BODY =
  'Sponsorship programs are not part of the pilot. Contact your pilot coordinator with any questions.';

const CRM_BLOCKED_BODY =
  'To keep client records in sync during the pilot, continue using your existing CRM workflow outside of Waterlily.';

/**
 * Every partner program, in precedence order: an advisor carrying more than one
 * flag resolves to the first match here, for organization identity, Intercom
 * segmentation and capability restrictions alike.
 */
export const PILOT_PROGRAMS: readonly PilotProgram[] = [
  {
    flagKey: 'edjPilotAdvisor',
    organizationDisplayName: EDWARD_JONES_ORGANIZATION_DISPLAY_NAME,
    organizationName: EDWARD_JONES_ORGANIZATION_NAME,
    lockedOrgLabel: 'the Edward Jones pilot',
    lockedOrgType: 'broker-dealer',
    intercomAdvisorType: 'edj_advisor',
    intercomClientType: 'edj_client',
    advisorBrandingMode: 'organization',
    restrictedCapabilities: [
      'add-partner',
      'anonymous-pui',
      'client-reminder',
      'crm-integration',
      'email-connection',
      'explore-quotes',
      'intake-form',
      'intake-form-data',
      'quick-edit-intake',
      'invite-client',
      // Clients of a pilot advisor see the Edward Jones name, never a logo the advisor
      // uploaded themselves. Withholds the logo upload and the client-header branding
      // choice; the whitelabel organization name stays, pinned to the program's own.
      'logo-branding',
      'org-type',
      'policies-comparison',
      'quick-start',
      'quote-apply',
    ],
    restrictedTooltip:
      'This feature is available on Waterlily but is not part of the Edward Jones pilot program.',
    blockedPages: {
      'crm-integration': {
        heading:
          'This feature is available on Waterlily but is not part of the Edward Jones pilot program.',
        body: CRM_BLOCKED_BODY,
      },
      'quote-apply': {
        heading: 'Quote & Apply is not available for Edward Jones pilot advisors',
        body: QUOTE_APPLY_BLOCKED_BODY,
      },
    },
    excludedOnboardingSteps: ['connect-email', 'consent'],
    waivedOnboardingSteps: ['consent', 'invite-client'],
    advisorSignupGate: {
      emailDomain: EDWARD_JONES_EMAIL_DOMAIN,
      ineligibleReason: 'edj_advisor_invite_required',
    },
    selfPlanIntakeForm: {
      flagKey: 'edjAdvisorPilotIntakeForm',
      acknowledgementText:
        'By using this service, I acknowledge that I do so solely as a Waterlily client for the purpose of assessing my own needs, and not in my capacity as an Edward Jones Financial Advisor or associate.',
      consentId: 'advisorEdjSelfPlanAcknowledgement',
    },
  },
  {
    flagKey: 'genworthMode',
    organizationDisplayName: GENWORTH_ORGANIZATION_DISPLAY_NAME,
    organizationName: GENWORTH_ORGANIZATION_NAME,
    lockedOrgLabel: 'Genworth',
    advisorBrandingMode: 'organization',
    waivedOnboardingSteps: ['activate-seat', 'consent', 'invite-client'],
    restrictedCapabilities: [],
    restrictedTooltip:
      'This feature is available on Waterlily but is not part of the Genworth experience.',
  },
  {
    flagKey: NYL_PILOT_ADVISOR_FEATURE_FLAG,
    organizationDisplayName: NEW_YORK_LIFE_ORGANIZATION_DISPLAY_NAME,
    organizationName: NEW_YORK_LIFE_ORGANIZATION_NAME,
    lockedOrgLabel: 'the New York Life pilot',
    lockedOrgType: 'wealth-management-firm',
    intercomAdvisorType: 'nyl_advisor',
    intercomClientType: 'nyl_client',
    advisorBrandingMode: 'waterlily',
    // New York Life withholds product recommendations, illustrations, underwriting
    // guidance, application functionality, production integrations, client edit rights,
    // advisor-to-advisor client sharing, the Team page, seat invitations and sponsorships for
    // the duration of the pilot. The one advisor who invites the pilot's members carries
    // `pilotTeamOwnerView`, which re-opens the Team page as owner only (see
    // `getPilotTeamAccess`); that inviter's team invitation also hands out a seat on their
    // subscription. Agents can still accept a seat; they just cannot invite anyone onto a
    // subscription. Its advisors all onboard under the one organization, so the organization type
    // is pinned rather than asked.
    restrictedCapabilities: [
      'anonymous-pui',
      'automatic-client-results',
      'carrier-bga',
      'client-sharing',
      'crm-integration',
      'distribution-partners',
      'email-connection',
      'engagement-campaigns',
      'explore-quotes',
      'intake-form',
      'logo-branding',
      'org-type',
      'policies-comparison',
      'quote-apply',
      'seat-invitations',
      'sponsorship',
      'team-dashboard',
    ],
    hiddenCapabilities: ['policies-comparison'],
    restrictedTooltip:
      'This feature is available on Waterlily but is not part of the New York Life pilot program.',
    blockedPages: {
      'quote-apply': {
        heading: 'Exploring Quotes is not available for New York Life pilot advisors.',
        body: QUOTE_APPLY_BLOCKED_BODY,
      },
      'crm-integration': {
        heading: 'CRM integrations are not available for New York Life pilot advisors',
        body: CRM_BLOCKED_BODY,
      },
      'team-dashboard': {
        heading: 'Teams are not available for New York Life pilot advisors',
        body: TEAM_DASHBOARD_BLOCKED_BODY,
      },
      'sponsorship': {
        heading: 'Sponsorships are not available for New York Life pilot advisors',
        body: SPONSORSHIP_BLOCKED_BODY,
      },
    },
    // New York Life agents accept the Terms of Service through the enterprise contract.
    excludedOnboardingSteps: ['connect-email', 'consent'],
    // The pilot's first-plan options cannot produce a client on their own, so requiring one
    // would leave these advisors unable to finish onboarding at all.
    waivedOnboardingSteps: ['consent', 'invite-client'],
    advisorSignupGate: {
      emailDomain: NEW_YORK_LIFE_EMAIL_DOMAIN,
      ineligibleReason: 'nyl_advisor_invite_required',
    },
    // New York Life committed that none of the withheld functionality is accessible,
    // which a UI-only control cannot promise: a browser holding a pre-deploy bundle
    // still renders every entry point.
    enforceRestrictionsAtApi: true,
    withholdsClientSupportChat: true,
    clientPlanReadOnly: true,
    selfPlanIntakeForm: {
      acknowledgementText:
        'By using this service, I acknowledge that I do so solely as a Waterlily client for the purpose of assessing my own needs, and not in my capacity as a New York Life agent or associate.',
      consentId: 'advisorNylSelfPlanAcknowledgement',
      reopensCapabilities: ['intake-form'],
    },
    agentEnteredIntake: {
      permissionText: NYL_AGENT_ENTERED_PERMISSION_TEXT,
      acknowledgementText: NYL_AGENT_ENTERED_DATA_USE_TEXT,
      permissionConsentId: 'clientAgentEnteredPermission',
      permissionConsentVersion: '2.0',
      acknowledgementConsentId: 'clientAgentEnteredAcknowledgement',
      acknowledgementConsentVersion: '2.0',
      reopensCapabilities: ['intake-form'],
    },
    communications: NYL_COMMUNICATION_POLICY,
    advisorOnlyPeriodFlagKey: 'nylPilotClientCreationRestricted',
  },
];

/** The program an advisor belongs to, or null when they belong to none. */
export function findActivePilotProgram(
  featureFlags: FeatureFlags | undefined | null,
): PilotProgram | null {
  if (!featureFlags) {
    return null;
  }

  return PILOT_PROGRAMS.find(program => featureFlags[program.flagKey]) ?? null;
}

export function isNylPilotAdvisor(featureFlags: FeatureFlags | undefined | null): boolean {
  return findActivePilotProgram(featureFlags)?.flagKey === NYL_PILOT_ADVISOR_FEATURE_FLAG;
}

/**
 * Resolves client-facing branding independently from the stored organization identity.
 * Legal, consent and disclosure flows continue to use the organization fields directly.
 */
export function resolveAdvisorBrandingMode(
  featureFlags: FeatureFlags | undefined | null,
): AdvisorBrandingMode {
  return findActivePilotProgram(featureFlags)?.advisorBrandingMode ?? 'organization';
}

/** The text lockup for headers and reports. Organization data itself remains unchanged. */
export function resolveAdvisorBrandingText(
  organizationDisplayName: string | null | undefined,
  brandingMode: AdvisorBrandingMode,
): { displayName: string; showPoweredByWaterlily: boolean } {
  const showOrganizationBranding =
    brandingMode === 'organization' && !!organizationDisplayName?.trim();

  return {
    displayName: showOrganizationBranding ? organizationDisplayName! : 'Waterlily',
    showPoweredByWaterlily: showOrganizationBranding,
  };
}

export function isPilotCapabilityRestricted(
  featureFlags: FeatureFlags | undefined | null,
  capability: PilotCapability,
): boolean {
  return !!findActivePilotProgram(featureFlags)?.restrictedCapabilities.includes(capability);
}

export function isPilotCapabilityHidden(
  featureFlags: FeatureFlags | undefined | null,
  capability: PilotCapability,
): boolean {
  return !!findActivePilotProgram(featureFlags)?.hiddenCapabilities?.includes(capability);
}

/**
 * Whether a request for this capability should be refused outright, rather than merely
 * hidden in the UI. True only for programs that opted into API enforcement.
 */
export function isPilotCapabilityRefusedAtApi(
  featureFlags: FeatureFlags | undefined | null,
  capability: PilotCapability,
): boolean {
  const program = findActivePilotProgram(featureFlags);

  return !!program?.enforceRestrictionsAtApi && program.restrictedCapabilities.includes(capability);
}

/** Whether the advisor is still in their program's advisor-only period, before client use is permitted. */
export function isPilotAdvisorOnlyPeriod(featureFlags: FeatureFlags | undefined | null): boolean {
  const flagKey = findActivePilotProgram(featureFlags)?.advisorOnlyPeriodFlagKey;

  return !!flagKey && !!featureFlags?.[flagKey];
}

export type PilotCommunicationHoldReason = 'disabled' | 'advisor-only-period' | 'awaiting-approval';

/**
 * `lockedBody`, when set, replaces the default body and anything the sender typed.
 */
export type PilotCommunicationDecision =
  | { send: true; lockedBody: string | null }
  | { send: false; reason: PilotCommunicationHoldReason };

/**
 * Whether an email the advisor's program reviewed may go out, and with what body.
 *
 * Pass the flags of the advisor the email is about: the client's advisor for a client email, the
 * recipient or sender for an advisor email. An advisor outside every program, or in a program with
 * no communications policy, gets the standard email.
 */
export function decidePilotCommunication(
  featureFlags: FeatureFlags | undefined | null,
  key: PilotCommunicationKey,
): PilotCommunicationDecision {
  const rule = findActivePilotProgram(featureFlags)?.communications?.[key];

  if (!rule) {
    return { send: true, lockedBody: null };
  }
  if (rule.disposition === 'disable') {
    return { send: false, reason: 'disabled' };
  }
  if (rule.waitsForClientUse && isPilotAdvisorOnlyPeriod(featureFlags)) {
    return { send: false, reason: 'advisor-only-period' };
  }
  if (rule.approval !== 'approved') {
    return { send: false, reason: 'awaiting-approval' };
  }

  return { send: true, lockedBody: rule.lockedBody ?? null };
}

/**
 * The partner's wording for an email whose text the advisor may not edit, or null when the
 * advisor's own text stands. Returned whether or not it is approved yet, so an editor can show the
 * text the client will receive once it is.
 */
export function getPilotLockedCommunicationBody(
  featureFlags: FeatureFlags | undefined | null,
  key: PilotCommunicationKey,
): string | null {
  const rule = findActivePilotProgram(featureFlags)?.communications?.[key];

  return rule && rule.disposition !== 'disable' ? (rule.lockedBody ?? null) : null;
}

/**
 * How much of the Team page an advisor gets.
 *
 * - `full`: their program does not withhold it, so the page is unchanged.
 * - `owner-only`: their program withholds it but they carry `pilotTeamOwnerView`; they open the
 *   page, see only the Owner view, and can invite. This is the pilot's designated inviter.
 * - `blocked`: their program withholds it and they are not the inviter.
 *
 * `PilotRestricted` cannot express this on its own because it only knows "restricted or not", so
 * the Team page, its sidebar entry and the invite service all ask this instead.
 */
export type PilotTeamAccess = 'full' | 'owner-only' | 'blocked';

export function getPilotTeamAccess(featureFlags: FeatureFlags | undefined | null): PilotTeamAccess {
  if (!isPilotCapabilityRestricted(featureFlags, 'team-dashboard')) {
    return 'full';
  }

  return featureFlags?.pilotTeamOwnerView ? 'owner-only' : 'blocked';
}

/** Whether clients of an advisor carrying these flags are kept out of Intercom chat. */
export function isClientSupportChatWithheld(
  featureFlags: FeatureFlags | undefined | null,
): boolean {
  return !!findActivePilotProgram(featureFlags)?.withholdsClientSupportChat;
}

/** Whether clients of an advisor carrying these flags see their plan read-only. */
export function isClientPlanReadOnly(featureFlags: FeatureFlags | undefined | null): boolean {
  return !!findActivePilotProgram(featureFlags)?.clientPlanReadOnly;
}

/** Onboarding requirements the advisor's program excuses. Empty when they belong to none. */
export function getPilotWaivedOnboardingSteps(
  featureFlags: FeatureFlags | undefined | null,
): readonly AdvisorOnboardingTrackingStep[] {
  return findActivePilotProgram(featureFlags)?.waivedOnboardingSteps ?? [];
}

/**
 * Whether the advisor's program excuses creating a first client.
 *
 * Gates the onboarding wizard's skip affordances. Without it the wizard demands a client the
 * program's own restrictions prevent the advisor from creating.
 */
export function isPilotFirstPlanWaived(featureFlags: FeatureFlags | undefined | null): boolean {
  return getPilotWaivedOnboardingSteps(featureFlags).includes('invite-client');
}

/** Onboarding steps the advisor's program waives. Empty when they belong to none. */
export function getPilotExcludedOnboardingSteps(
  featureFlags: FeatureFlags | undefined | null,
): readonly AdvisorOnboardingUIStep[] {
  return findActivePilotProgram(featureFlags)?.excludedOnboardingSteps ?? [];
}

/** The program whose advisor sign-up is invitation-only for this email's domain; null when none. */
export function findAdvisorSignupGatedProgram(
  email: string | null | undefined,
): SignupGatedPilotProgram | null {
  if (!email) {
    return null;
  }

  const normalizedEmail = email.trim().toLowerCase();

  return (
    PILOT_PROGRAMS.find(
      (program): program is SignupGatedPilotProgram =>
        !!program.advisorSignupGate &&
        normalizedEmail.endsWith(`@${program.advisorSignupGate.emailDomain}`),
    ) ?? null
  );
}

/** The program a sign-up eligibility check refused an email for; null when the reason is unknown. */
export function findPilotProgramBySignupIneligibleReason(
  reason: PilotSignupIneligibleReason | null | undefined,
): SignupGatedPilotProgram | null {
  if (!reason) {
    return null;
  }

  return (
    PILOT_PROGRAMS.find(
      (program): program is SignupGatedPilotProgram =>
        program.advisorSignupGate?.ineligibleReason === reason,
    ) ?? null
  );
}

/** The agreement the advisor's program defines, whether or not its switch is on; null when none. */
export function getPilotParticipationAgreement(
  featureFlags: FeatureFlags | undefined | null,
): PilotParticipationAgreement | null {
  return findActivePilotProgram(featureFlags)?.participationAgreement ?? null;
}

export function getNylParticipationAgreement(): PilotParticipationAgreement | null {
  return getPilotParticipationAgreement({ [NYL_PILOT_ADVISOR_FEATURE_FLAG]: true });
}

/**
 * The agreement the advisor must accept before platform access: their program's, while its AppConfig
 * switch is on. The redirect and the accept service both ask this, so the switch alone decides
 * whether anyone is held or can record an acceptance.
 *
 * `appConfigFlags` is null when AppConfig could not be read, and the agreement is then required: an
 * outage must not let an agent in without accepting. Only an explicit "off" waives it.
 */
export function getRequiredParticipationAgreement(
  featureFlags: FeatureFlags | undefined | null,
  appConfigFlags: AppConfigFeatureFlags | null,
): PilotParticipationAgreement | null {
  const agreement = getPilotParticipationAgreement(featureFlags);
  if (!agreement) {
    return null;
  }

  return appConfigFlags === null || appConfigFlags[agreement.appConfigFlagKey] ? agreement : null;
}

/**
 * The self-plan intake form for this advisor, or null when their program has none or
 * the form's own gating flag is off.
 */
export function getPilotSelfPlanIntakeForm(
  featureFlags: FeatureFlags | undefined | null,
): PilotSelfPlanIntakeForm | null {
  const selfPlanIntakeForm = findActivePilotProgram(featureFlags)?.selfPlanIntakeForm;

  if (!selfPlanIntakeForm) {
    return null;
  }

  if (selfPlanIntakeForm.flagKey && !featureFlags?.[selfPlanIntakeForm.flagKey]) {
    return null;
  }

  return selfPlanIntakeForm;
}

/**
 * Whether a withheld capability is open for this client because it is the advisor's own self-plan.
 * `advisorId` is the signed-in advisor: a self-plan client belonging to someone else stays withheld.
 */
export function isPilotCapabilityReopenedForSelfPlan(
  featureFlags: FeatureFlags | undefined | null,
  capability: PilotCapability,
  client: Pick<Client, 'advisorId' | 'isAdvisorSelfPlan'> | null | undefined,
  advisorId: string | null | undefined,
): boolean {
  return (
    !!client?.isAdvisorSelfPlan &&
    !!advisorId &&
    client.advisorId === advisorId &&
    !!getPilotSelfPlanIntakeForm(featureFlags)?.reopensCapabilities?.includes(capability)
  );
}

/**
 * The only question the feature asks anywhere: non-null means this advisor may initiate an
 * agent-entered case. There is no separate flag.
 */
export function getPilotAgentEnteredIntake(
  featureFlags: FeatureFlags | undefined | null,
): PilotAgentEnteredIntake | null {
  return findActivePilotProgram(featureFlags)?.agentEnteredIntake ?? null;
}

/**
 * Distinct from `isPilotCapabilityRestricted`, which stays true — only the consented variant is
 * reachable. Callers showing the consented entry point ask this one.
 */
export function isPilotCapabilityReopenedByConsent(
  featureFlags: FeatureFlags | undefined | null,
  capability: PilotCapability,
): boolean {
  return !!getPilotAgentEnteredIntake(featureFlags)?.reopensCapabilities?.includes(capability);
}

/**
 * Partner programs that lock the advisor's organization name to a fixed value
 * (preset and non-editable during onboarding). Returns the locked names, or null
 * when the advisor's flags don't pin them to a partner organization.
 */
export function getLockedAdvisorOrgNames(
  featureFlags: FeatureFlags | undefined | null,
): { organizationDisplayName: string; organizationName: string } | null {
  const program = findActivePilotProgram(featureFlags);

  if (!program) {
    return null;
  }

  return {
    organizationDisplayName: program.organizationDisplayName,
    organizationName: program.organizationName,
  };
}

/**
 * The organization type preset by the advisor's program, or null when their flags don't
 * pin one. Callers use it both to prefill the onboarding field and to decide what to
 * persist, so a program's advisors never save a type they were never asked for.
 */
export function getLockedAdvisorOrgType(
  featureFlags: FeatureFlags | undefined | null,
): OrgType | null {
  return findActivePilotProgram(featureFlags)?.lockedOrgType ?? null;
}

export const PILOT_RELATIONSHIP_ACCESS_TYPES = [
  'no_client_access',
  'two_way',
  'new_user_sees_related_clients',
  'related_user_sees_new_user_clients',
] as const;

export const PILOT_PROGRAM_FEATURE_FLAG_KEYS = [
  'wizardMode',
  'vbShop',
  'transamerica',
  'fortifiedLife',
  'newYorkLife',
  'ltciPartners',
  'agis',
  'shopForInsurance',
  'quoteApplyCareScout',
  'quoteApplyLincolnFinancial',
  'quoteApplyEquiTrust',
  'quoteApplyNewYorkLife',
  'traditionalLTCPaymentOptions',
  'hideSinglePayAnd10Pay',
  'licensesManagement',
  'd2cMode',
  'edjPilotAdvisor',
  'edjAdvisorPilotIntakeForm',
  'genworthMode',
  'nylPilotAdvisor',
  'pilotTeamOwnerView',
  'ccrcTypeAProducts',
  'financialFeasibility',
  'freemiumAdvisor',
  'quoteApplyManhattanLife',
  'twoFactorAuth',
] as const satisfies readonly FeatureFlagKeys[];

export type PilotRelationshipAccessType = (typeof PILOT_RELATIONSHIP_ACCESS_TYPES)[number];

export type PilotRelationshipKind = 'default_supervisor' | 'associated_staff';

export interface PilotOnboardingProgramRoleConfiguration {
  key: string;
  label: string;
  shortLabel: string;
  defaultSubscriptionOwnerEmail: string;
  featureFlags?: FeatureFlags;
  isActive: boolean;
  sortOrder: number;
}

export interface PilotOnboardingProgramSupervisorConfiguration {
  email: string;
  accessType: PilotRelationshipAccessType;
  isActive: boolean;
  sortOrder: number;
}

export interface PilotOnboardingProgramConfiguration {
  featureFlags: FeatureFlags;
  defaultSeatExpiresDateTime: string | null;
  roles: PilotOnboardingProgramRoleConfiguration[];
  defaultSupervisors: PilotOnboardingProgramSupervisorConfiguration[];
}

/** Role-level selections are authoritative once present. */
export function resolvePilotRoleFeatureFlags(
  programFeatureFlags: FeatureFlags,
  roleFeatureFlags?: FeatureFlags,
): FeatureFlags {
  if (roleFeatureFlags === undefined) return { ...programFeatureFlags };
  return { ...roleFeatureFlags };
}

export interface PilotOnboardingRelationshipSelection {
  relatedAdvisorId: string | null;
  relatedAdvisorEmail: string;
  kind: PilotRelationshipKind;
  accessType: PilotRelationshipAccessType;
}

export interface PilotOnboardingRequestSnapshot {
  role: PilotOnboardingProgramRoleConfiguration;
  featureFlags: FeatureFlags;
  relationships: PilotOnboardingRelationshipSelection[];
}

export interface PreRegisteredFeatureFlagArtifact {
  target: 'pre_registered_feature_flags';
  id: string;
  created: boolean;
  previousFeatureFlags?: FeatureFlags;
  appliedFeatureFlags: FeatureFlags;
}

export interface PreRegisteredRelationshipArtifact {
  target: 'pre_registered_team_relationship';
  id: string;
  created: boolean;
  previousAccessMode?: ClientAccessMode;
  previousEnabledClientAccess?: boolean;
  appliedAccessMode: ClientAccessMode;
}

export interface AdvisorHierarchyArtifact {
  target: 'advisor_hierarchy';
  supervisorAdvisorId: string;
  subordinateAdvisorId: string;
  created: boolean;
  previousIsActive?: boolean;
  previousAccessMode?: ClientAccessMode;
  previousEnabledClientAccess?: boolean;
  appliedAccessMode: ClientAccessMode;
}

export type PilotOnboardingArtifact =
  | PreRegisteredFeatureFlagArtifact
  | PreRegisteredRelationshipArtifact
  | AdvisorHierarchyArtifact;

export type PilotOnboardingArtifactTarget = PilotOnboardingArtifact['target'];

export function isPilotOnboardingArtifact(value: unknown): value is PilotOnboardingArtifact {
  if (!value || typeof value !== 'object') return false;
  const artifact = value as Record<string, unknown>;
  if (typeof artifact.created !== 'boolean') return false;

  switch (artifact.target) {
    case 'pre_registered_feature_flags':
      return (
        typeof artifact.id === 'string' &&
        !!artifact.appliedFeatureFlags &&
        typeof artifact.appliedFeatureFlags === 'object'
      );
    case 'pre_registered_team_relationship':
      return typeof artifact.id === 'string' && typeof artifact.appliedAccessMode === 'string';
    case 'advisor_hierarchy':
      return (
        typeof artifact.supervisorAdvisorId === 'string' &&
        typeof artifact.subordinateAdvisorId === 'string' &&
        typeof artifact.appliedAccessMode === 'string'
      );
    default:
      return false;
  }
}

export function selectPilotOnboardingArtifactsByTarget<
  TTarget extends PilotOnboardingArtifactTarget,
>(
  artifacts: Record<string, unknown> | undefined,
  target: TTarget,
): Array<Extract<PilotOnboardingArtifact, { target: TTarget }>> {
  if (!artifacts) return [];
  const rawRecords = Array.isArray(artifacts.records)
    ? artifacts.records
    : isPilotOnboardingArtifact(artifacts)
      ? [artifacts]
      : [];
  return rawRecords.filter(
    (artifact): artifact is Extract<PilotOnboardingArtifact, { target: TTarget }> =>
      isPilotOnboardingArtifact(artifact) && artifact.target === target,
  );
}

export type PilotOnboardingSource = 'pilot_onboarding' | 'legacy';

export type PilotOnboardingStatus =
  | 'pending'
  | 'processing'
  | 'needs_attention'
  | 'invited'
  | 'active'
  | 'declined'
  | 'expired'
  | 'deleted';

export const PILOT_ONBOARDING_PROCESSING_LEASE_MS = 5 * 60 * 1000;

export type PilotOnboardingAuditAction =
  | 'created'
  | 'retry_requested'
  | 'invitation_resent'
  | 'edited'
  | 'replacement_requested'
  | 'deleted'
  | 'accepted'
  | 'declined';

export interface PilotOnboardingAuditEvent {
  action: PilotOnboardingAuditAction;
  actorAdvisorId: string | null;
  at: string;
  details?: Record<string, unknown>;
}

export type PilotOnboardingStepKey = 'flags' | 'relationships' | 'seat_invitation';

export type PilotOnboardingStepStatus = 'pending' | 'processing' | 'succeeded' | 'failed';

export interface PilotOnboardingStepState {
  status: PilotOnboardingStepStatus;
  attempts: number;
  errorCode?: string;
  errorStage?: string;
  errorDetails?: Record<string, unknown>;
  artifacts?: Record<string, unknown>;
  updatedAt?: string;
}

export type PilotOnboardingSteps = Record<PilotOnboardingStepKey, PilotOnboardingStepState>;

export const EMPTY_PILOT_ONBOARDING_STEPS: PilotOnboardingSteps = {
  flags: { status: 'pending', attempts: 0 },
  relationships: { status: 'pending', attempts: 0 },
  seat_invitation: { status: 'pending', attempts: 0 },
};
